import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { FaBarcode, FaCheckCircle, FaImage, FaKeyboard, FaList, FaSearch, FaThLarge } from "react-icons/fa";
import productService from "../../services/productService";
import { formatPrice } from "../../helpers/utility";
import config from "../../helpers/config";
import POSKeypadModal from "../Comptoire/POSKeypadModal";
import "./ReturnArticlePicker.css";

export const articleToReturnLine = (product) => {
  const tauxTVA = Number(product.tauxTVA ?? product.tauxTVAVente ?? 0.2);
  const prixUnitaireTTC = Number(product.prixVenteTTC1 ?? 0);
  return {
    article: product.id,
    articleId: product.id,
    articleLibelle: product.libelle,
    articleCode: product.codeBarre || "",
    quantiteRetournee: 1,
    prixUnitaireHT: Number(product.prixVenteHT ?? prixUnitaireTTC / (1 + tauxTVA)),
    prixUnitaireTTC,
    tauxTVA,
    unite: product.unite || "",
  };
};

export default function ReturnArticlePicker({ lines, onAdd, disabled }) {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [result, setResult] = useState({ content: [], totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [displayMode, setDisplayMode] = useState("list");
  const [keypadOpen, setKeypadOpen] = useState(false);
  const unites = useSelector(state => state.products?.unites || []);
  const changeSearch = value => { setQuery(value); setPage(0); };
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    const timer = setTimeout(async () => {
      try {
        const response = await productService.findAll(query.trim(), page, 24);
        if (active) setResult(response.body);
      } catch {
        if (active) setError("Impossible de charger les articles. Réessayez votre recherche.");
      } finally {
        if (active) setLoading(false);
      }
    }, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [query, page]);

  return <div className="return-article-picker mb-3">
    <label className="form-label fw-bold" htmlFor="return-article-search">Articles à retourner</label>
    <div className="return-article-searchbar">
      <div className="return-article-search-input">
        <FaSearch className="return-search-icon" aria-hidden="true" />
        <input id="return-article-search" className="form-control" placeholder="Rechercher par libellé ou code-barres"
          value={query} readOnly inputMode="none" aria-haspopup="dialog"
          onClick={() => setKeypadOpen(true)} onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setKeypadOpen(true); }
          }} />
        <FaBarcode className="return-barcode-icon" aria-hidden="true" />
      </div>
      <button type="button" className="btn btn-secondary" onClick={() => setKeypadOpen(true)} aria-label="Ouvrir le clavier de recherche"><FaKeyboard /></button>
    </div>
    <div className="return-article-viewbar">
      <small className="text-muted">{result?.totalElements ?? result?.content?.length ?? 0} article(s)</small>
      <div className="d-flex gap-2" role="group" aria-label="Affichage des articles">
        <button type="button" className={`btn btn-${displayMode === 'card' ? 'primary' : 'light'} border`} aria-pressed={displayMode === 'card'} onClick={() => setDisplayMode('card')}><FaThLarge className="me-2" />Cartes</button>
        <button type="button" className={`btn btn-${displayMode === 'list' ? 'primary' : 'light'} border`} aria-pressed={displayMode === 'list'} onClick={() => setDisplayMode('list')}><FaList className="me-2" />Liste</button>
      </div>
    </div>
    {loading ? <p role="status">Chargement des articles…</p> : error ? <p role="alert" className="text-danger">{error}</p> : <>
      <div className={displayMode === 'list' ? "return-article-list" : "return-article-grid"}>
        {(result?.content || []).map(product => {
          const selected = lines.some(line => String(line.articleId) === String(product.id));
          const unitLabel = unites.find(unit => unit.code === product.unite)?.libelle || product.unite || "—";
          return <button key={product.id} type="button" className={displayMode === 'list' ? "return-article-row" : "return-article-card"}
            disabled={disabled || selected} aria-label={`${product.libelle}${selected ? ' — Déjà ajouté' : ' — Ajouter au retour'}`}
            onClick={() => onAdd(articleToReturnLine(product))}>
            {displayMode === 'card' && <span className="return-article-image">
              {product.photo ? <img src={product.photo.startsWith('http') ? product.photo : `${config.baseURL}${product.photo}`} alt="" loading="lazy" /> : <FaImage size={28} className="text-muted" />}
            </span>}
            <span className="return-article-info">
              <span className="fw-bold">{product.libelle || "—"}</span>
              <span className="small text-muted">Code : {product.codeBarre || "—"}</span>
              <span className="small text-muted">Stock : {product.stockDisponible ?? 0} | Unité : {unitLabel}</span>
              {selected && <span className="small text-success"><FaCheckCircle className="me-1" />Déjà ajouté</span>}
            </span>
            <span className="return-article-price">{formatPrice(product.prixVenteTTC1 ?? 0)}</span>
          </button>;
        })}
      </div>
      {!result?.content?.length && <p className="text-muted">Aucun article trouvé.</p>}
      {result?.totalPages > 1 && <div className="d-flex align-items-center gap-2 mt-2">
        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page === 0} onClick={() => setPage(page - 1)}>Précédent</button>
        <span>{page + 1} / {result.totalPages}</span>
        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page + 1 >= result.totalPages} onClick={() => setPage(page + 1)}>Suivant</button>
      </div>}
    </>}
    <POSKeypadModal isOpen={keypadOpen} toggle={() => setKeypadOpen(false)} value={query}
      onChange={changeSearch} onSearch={() => setKeypadOpen(false)} title="Recherche article" />
  </div>;
}
